import { EditorIcon } from "@/components/environments/EditorIcon";
import { Button } from "@/components/flexkit/Button";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Switch } from "@/components/Switch";
import { Text } from "@/components/podkit/typography/Text";
import { useListEditors, type PlainEditor } from "@/queries/editor-queries";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/podkit/dropdown/DropDown";
import { ChevronDown, X, Info } from "lucide-react";
import * as semver from "semver";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { Label } from "@/components/podkit/forms/Label";
import { TrackLocations } from "@/hooks/use-segment";

export type RecommendedEditorsValue = Record<string, string[]>;

type RecommendedEditorsSelectProps = {
    value: RecommendedEditorsValue;
    onChange: (value: RecommendedEditorsValue) => void;
    disabled?: boolean;
};

export const RecommendedEditorsSelect: FC<RecommendedEditorsSelectProps> = ({ value, onChange, disabled = false }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { data: editors, isLoading } = useListEditors({ allowedByPolicy: true });

    const selectedEditors = useMemo(() => {
        if (!editors?.editors) return [];
        return editors.editors.filter((editor) => editor.alias && editor.alias in value);
    }, [editors, value]);

    const handleSave = useCallback(
        (newValue: RecommendedEditorsValue) => {
            onChange(newValue);
            setIsModalOpen(false);
        },
        [onChange],
    );

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
                <Text className="text-sm text-content-secondary">
                    {selectedEditors.length === 0
                        ? "No editors recommended"
                        : `${selectedEditors.length} editor${selectedEditors.length > 1 ? "s" : ""} recommended`}
                </Text>
                <Button
                    disabled={disabled}
                    onClick={() => setIsModalOpen(true)}
                    variant="secondary"
                    size="sm"
                    data-testid="manage-recommended-editors-button"
                    data-tracking-id="manage-recommended-editors-element"
                >
                    {selectedEditors.length === 0 ? "Add" : "Manage"}
                </Button>
            </div>
            <SkeletonBlock ready={!isLoading}>
                {selectedEditors.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {selectedEditors.map((editor) => {
                            const versions = editor.alias ? value[editor.alias] : [];
                            return (
                                <EditorPill
                                    key={editor.id}
                                    editor={editor}
                                    versions={versions}
                                    onRemove={
                                        disabled
                                            ? undefined
                                            : () => {
                                                  if (!editor.alias) return;
                                                  const newValue = { ...value };
                                                  delete newValue[editor.alias];
                                                  onChange(newValue);
                                              }
                                    }
                                />
                            );
                        })}
                    </div>
                )}
            </SkeletonBlock>
            {isModalOpen && editors?.editors && (
                <RecommendedEditorsModal
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSave}
                    editors={editors.editors}
                    initialValue={value}
                    disabled={disabled}
                />
            )}
        </div>
    );
};

const EditorPill: FC<{
    editor: PlainEditor;
    versions?: string[];
    onRemove?: () => void;
}> = ({ editor, versions, onRemove }) => {
    const hasVersions = versions && versions.length > 0;

    return (
        <div className="inline-flex h-9 select-none items-center gap-2 whitespace-nowrap rounded-xl border-0.5 border-border-base bg-surface-primary p-2 px-3 text-base font-medium">
            <EditorIcon editor={editor} size="base" />
            <span>{editor.name}</span>
            {hasVersions && (
                <span className="rounded-md bg-surface-secondary px-2 py-0.5 text-xs text-content-secondary">
                    {versions.join(", ")}
                </span>
            )}
            {onRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    className="ml-1 rounded-full p-0.5 hover:bg-surface-secondary"
                    aria-label={`Remove ${editor.name}`}
                    data-tracking-id="remove-recommended-editors-version-button"
                >
                    <X className="h-3 w-3 text-content-secondary" />
                </button>
            )}
        </div>
    );
};

type ModalProps = {
    onClose: () => void;
    onSave: (value: RecommendedEditorsValue) => void;
    editors: PlainEditor[];
    initialValue: RecommendedEditorsValue;
    disabled?: boolean;
};

const RecommendedEditorsModal: FC<ModalProps> = ({ onClose, onSave, editors, initialValue, disabled = false }) => {
    const [selectedEditors, setSelectedEditors] = useState<Record<string, boolean>>(() => {
        const selected: Record<string, boolean> = {};
        editors.forEach((editor) => {
            if (editor.alias) {
                selected[editor.alias] = editor.alias in initialValue;
            }
        });
        return selected;
    });

    const [editorVersions, setEditorVersions] = useState<Record<string, string[]>>(() => {
        return { ...initialValue };
    });

    const [isDirty, setIsDirty] = useState(false);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const handleSave = useCallback(() => {
        const result: RecommendedEditorsValue = {};
        Object.entries(selectedEditors).forEach(([alias, isSelected]) => {
            if (isSelected) {
                result[alias] = editorVersions[alias] || [];
            }
        });
        onSave(result);
    }, [selectedEditors, editorVersions, onSave]);

    const handleEditorSelectionChange = useCallback((alias: string, checked: boolean) => {
        setSelectedEditors((prev) => ({ ...prev, [alias]: checked }));
        setIsDirty(true);
    }, []);

    const handleVersionChange = useCallback((alias: string, versions: string[]) => {
        setEditorVersions((prev) => ({
            ...prev,
            [alias]: versions.sort((a, b) => {
                const va = semver.coerce(a);
                const vb = semver.coerce(b);
                if (va === null || vb === null) {
                    return b.localeCompare(a);
                }
                return semver.compare(vb, va);
            }),
        }));
        setIsDirty(true);
    }, []);

    // Filter editors that have an alias
    const editorsWithAlias = useMemo(() => editors.filter((e) => !!e.alias), [editors]);

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="recommended-editors-modal"
                data-track-location={TrackLocations.RecommendedEditorsModal}
            >
                <DialogHeader>
                    <DialogTitle>Recommended editors</DialogTitle>
                    <DialogDescription>
                        Select editors to recommend for this project. Users will see these as suggested options.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="max-h-[400px] space-y-1 overflow-y-auto">
                    {editorsWithAlias.map((editor, index) => {
                        const alias = editor.alias;
                        const availableVersions = editor.versions?.map((v) => v.version) || [];
                        const hasVersionSupport = availableVersions.length > 0;
                        const selectedVersions = editorVersions[alias] || [];
                        const hasVersionRestrictions = selectedVersions.length > 0;
                        const isSelected = selectedEditors[alias];

                        return (
                            <div key={editor.id}>
                                <div className="group flex min-h-[36px] flex-row items-center gap-3">
                                    <Switch
                                        disabled={disabled}
                                        state={isSelected ? "checked" : "unchecked"}
                                        onToggle={(checked) => handleEditorSelectionChange(alias, checked)}
                                        id={`editor-toggle-${index}`}
                                    />
                                    <div className="flex flex-row items-center gap-2">
                                        <EditorIcon editor={editor} size="base" />
                                        <span>{editor.name}</span>
                                    </div>
                                    <div className="ml-auto flex items-center gap-2">
                                        {hasVersionSupport && isSelected && (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        disabled={disabled}
                                                        variant="secondary"
                                                        size="md"
                                                        className="font-sm h-7 gap-1 rounded-lg px-2 py-1.5 font-sans text-sm tracking-tight text-content-primary"
                                                    >
                                                        add version
                                                        <ChevronDown className="h-3 w-3 opacity-60" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                    align="end"
                                                    className="min-w-[200px] !pb-0"
                                                    onCloseAutoFocus={(e) => e.preventDefault()}
                                                >
                                                    {availableVersions.map((version, idx) => (
                                                        <Label
                                                            key={version}
                                                            className="relative flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-surface-hover"
                                                        >
                                                            <Checkbox
                                                                id={`${alias}-${version}`}
                                                                checked={selectedVersions.includes(version)}
                                                                onCheckedChange={(checked) => {
                                                                    const newVersions = checked
                                                                        ? [...selectedVersions, version]
                                                                        : selectedVersions.filter((v) => v !== version);
                                                                    handleVersionChange(alias, newVersions);
                                                                }}
                                                            />
                                                            {version}
                                                            {idx === 0 && (
                                                                <span className="ml-auto rounded-md bg-surface-secondary px-2 py-0.5 text-xs text-content-secondary">
                                                                    latest
                                                                </span>
                                                            )}
                                                        </Label>
                                                    ))}
                                                    {selectedVersions.length === 0 && (
                                                        <div className="flex items-start gap-2 bg-surface-button-clear-accent px-3 py-2">
                                                            <Info className="mt-0.5 h-4 w-4 shrink-0 text-content-brand" />
                                                            <span className="text-sm text-content-brand">
                                                                No selection means all versions are recommended
                                                            </span>
                                                        </div>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                    </div>
                                </div>
                                {hasVersionSupport && hasVersionRestrictions && isSelected && (
                                    <div className="ml-12 flex flex-wrap items-center gap-1 pb-2">
                                        {selectedVersions.map((version) => (
                                            <span
                                                key={version}
                                                className="inline-flex h-6 items-center gap-0.5 rounded-[20px] bg-surface-brand px-2 py-1 font-mono text-xs font-normal text-[rgb(var(--ona-blue-500))]"
                                            >
                                                {version}
                                                <button
                                                    type="button"
                                                    disabled={disabled}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const newVersions = selectedVersions.filter(
                                                            (v) => v !== version,
                                                        );
                                                        handleVersionChange(alias, newVersions);
                                                    }}
                                                    className="ml-0.5 rounded-full hover:bg-surface-secondary"
                                                    data-tracking-id="remove-version-button"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="secondary">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={disabled || !isDirty}
                        data-tracking-id="save"
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
